import { supabaseAEC as supabase } from './supabaseAEC';

/**
 * Fetch tools from v_tools_published with optional filters.
 * Category filtering requires a sub-query through tool_categories.
 */
/**
 * Build and run the main tool-listing query.
 * viewCols controls which optional columns (that may not exist in the view) are applied.
 */
async function _runFetchTools({
  categoryToolIds,
  search,
  pricingModels,
  platforms,
  disciplines,
  freeTrialOnly,
  viewCols = { platforms: true, disciplines: true, freeTrialOnly: true },
}) {
  let query = supabase.from('v_tools_published').select('*');

  if (search) {
    query = query.or(
      `name.ilike.%${search}%,tagline.ilike.%${search}%,vendor_name.ilike.%${search}%`
    );
  }

  if (categoryToolIds !== null) {
    query = query.in('id', categoryToolIds);
  }

  if (pricingModels.length === 1) {
    query = query.eq('pricing_model', pricingModels[0]);
  } else if (pricingModels.length > 1) {
    query = query.in('pricing_model', pricingModels);
  }

  if (viewCols.platforms && platforms.length > 0) {
    query = query.overlaps('platforms', platforms);
  }

  if (viewCols.disciplines && disciplines.length > 0) {
    query = query.overlaps('disciplines', disciplines);
  }

  if (viewCols.freeTrialOnly && freeTrialOnly) {
    query = query.eq('has_free_trial', true);
  }

  query = query.order('is_sponsored', { ascending: false });

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export async function fetchTools({
  search = '',
  categoryId = null,
  pricingModels = [],
  platforms = [],
  disciplines = [],
  freeTrialOnly = false,
} = {}) {
  // Resolve category → tool IDs first
  let categoryToolIds = null;
  if (categoryId) {
    const { data, error } = await supabase
      .from('tool_categories')
      .select('tool_id')
      .eq('category_id', categoryId);
    if (error) throw error;
    categoryToolIds = (data ?? []).map((r) => r.tool_id);
    if (categoryToolIds.length === 0) return [];
  }

  const args = { categoryToolIds, search, pricingModels, platforms, disciplines, freeTrialOnly };

  try {
    return await _runFetchTools({ ...args });
  } catch (err) {
    // One or more optional columns (platforms, disciplines, has_free_trial) don't exist in
    // v_tools_published yet. Retry progressively without the missing-column filters so the
    // library still works and results are filtered client-side instead.
    if (err?.code !== '42703') throw err;

    console.warn('[fetchTools] view missing column — retrying without array-overlap filters:', err.message);
    try {
      // Retry without platforms/disciplines/freeTrialOnly (they require extra view columns)
      const data = await _runFetchTools({
        ...args,
        viewCols: { platforms: false, disciplines: false, freeTrialOnly: false },
      });
      // Apply remaining filters client-side
      return data.filter((t) => {
        if (platforms.length > 0 && !platforms.some((p) => t.platforms?.includes(p))) return false;
        if (disciplines.length > 0 && !disciplines.some((d) => t.disciplines?.includes(d))) return false;
        if (freeTrialOnly && !t.has_free_trial) return false;
        return true;
      });
    } catch (err2) {
      throw err2;
    }
  }
}

/**
 * Fetch multiple tools by slug array — used by the compare page.
 * Falls back to the base tools table for child tools (plans/addons) that are
 * not present in v_tools_published, so variations can be compared directly.
 * Returns in the same order as the slugs array.
 */
export async function fetchToolsBySlugs(slugs) {
  if (!slugs.length) return [];
  const { data, error } = await supabase
    .from('v_tools_published')
    .select('*')
    .in('slug', slugs);
  if (error) throw error;

  // Slugs not found in the view are child tools — fetch from base table
  const found = new Set((data ?? []).map((t) => t.slug));
  const missing = slugs.filter((s) => !found.has(s));
  let all = [...(data ?? [])];

  if (missing.length) {
    const { data: extras } = await supabase
      .from('tools')
      .select('*, tool_meta(*)')
      .in('slug', missing);
    for (const t of extras ?? []) {
      all.push({
        ...t,
        // Normalise to the shape AECCompare expects from the view
        price_from:     t.tool_meta?.price_from     ?? t.price_from     ?? null,
        price_currency: t.tool_meta?.price_currency ?? t.price_currency ?? 'USD',
        billing_cycle:  t.tool_meta?.billing_cycle  ?? t.billing_cycle  ?? null,
        avg_rating:    null,
        review_count:  0,
        vendor_name:   null,
        is_sponsored:  false,
        integrations:  t.tool_meta?.integrations    ?? [],
      });
    }
  }

  return slugs.map((s) => all.find((t) => t.slug === s)).filter(Boolean);
}

/**
 * Fetch a single published tool by slug.
 * Merges data from the view (ratings, vendor name) with the tools table
 * (description_long, billing details) and tool_meta (integrations detail).
 */
export async function fetchToolBySlug(slug) {
  const [viewRes, detailRes] = await Promise.all([
    supabase.from('v_tools_published').select('*').eq('slug', slug).single(),
    supabase
      .from('tools')
      .select('description_long, billing_cycle, trial_days, pricing_notes, pricing_page_url, tool_meta(*)')
      .eq('slug', slug)
      .single(),
  ]);
  if (viewRes.error) throw viewRes.error;
  const detail = detailRes.data ?? {};
  return {
    ...viewRes.data,
    description_long: detail.description_long ?? null,
    billing_cycle: detail.billing_cycle ?? null,
    trial_days: detail.trial_days ?? null,
    pricing_notes: detail.pricing_notes ?? null,
    pricing_page_url: detail.pricing_page_url ?? null,
    tool_meta: detail.tool_meta ?? null,
  };
}

/**
 * Normalise a raw tool_features row (joined with feature_definitions) into the
 * shape the UI expects: feature_name, value_type, value_boolean, sort_order.
 */
function _normaliseFeature(row) {
  const fd = row.feature_definitions ?? {};
  return {
    ...row,
    feature_name:     fd.name             ?? '',
    feature_slug:     fd.slug             ?? '',
    feature_category: fd.feature_category ?? null,
    value_type:       fd.value_type       ?? 'boolean',
    value_boolean:    row.is_available    ?? null,
    value_text:       row.value           ?? null,
    sort_order:       fd.sort_order       ?? 0,
  };
}

/**
 * Fetch tool_features for a single tool, joined with feature_definitions.
 */
export async function fetchToolFeatures(toolId) {
  const { data, error } = await supabase
    .from('tool_features')
    .select('*, feature_definitions(name, slug, value_type, sort_order, feature_category)')
    .eq('tool_id', toolId);
  if (error) throw error;
  return (data ?? [])
    .map(_normaliseFeature)
    .sort((a, b) => a.sort_order - b.sort_order);
}

/**
 * Batch-fetch tool_features for multiple tool IDs (used by VariationsTable).
 * Returns { [toolId]: normalised feature array }
 */
export async function fetchToolFeaturesMany(toolIds) {
  if (!toolIds.length) return {};
  const { data, error } = await supabase
    .from('tool_features')
    .select('*, feature_definitions(name, slug, value_type, sort_order, feature_category)')
    .in('tool_id', toolIds);
  if (error) throw error;
  const map = {};
  for (const row of data ?? []) {
    if (!map[row.tool_id]) map[row.tool_id] = [];
    map[row.tool_id].push(_normaliseFeature(row));
  }
  for (const id of toolIds) {
    if (map[id]) map[id].sort((a, b) => a.sort_order - b.sort_order);
  }
  return map;
}

/**
 * Fetch published reviews for a tool.
 * published_verified reviews sort first, then published_anonymous.
 * Within each group: helpful_votes DESC, created_at DESC.
 */
export async function fetchToolReviews(toolId) {
  const { data, error } = await supabase
    .from('reviews')
    .select('id, display_name, reviewer_name_input, reviewer_job_title_input, show_job_title, show_company, reviewer_company_input, rating, title, body, pros, cons, status, helpful_votes, published_at, created_at')
    .eq('tool_id', toolId)
    .in('status', ['published_verified', 'published_anonymous'])
    .order('status', { ascending: false })          // 'published_verified' > 'published_anonymous'
    .order('helpful_votes', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch category pills for a single tool.
 * Path: task_tool → task_usecase.category_id → categories
 * Deduplicates categories (a tool may map to multiple tasks in the same category).
 */
export async function fetchToolCategories(toolId) {
  const { data, error } = await supabase
    .from('task_tool')
    .select('task_usecase(categories(id, name, slug))')
    .eq('tool_id', toolId);
  if (error) throw error;
  const seen = new Set();
  return (data ?? [])
    .map((r) => r.task_usecase?.categories)
    .filter((c) => c && !seen.has(c.id) && seen.add(c.id));
}

/**
 * Submit a new review.
 *
 * One review per email per tool is enforced by a partial unique index
 * on (tool_id, reviewer_email) WHERE reviewer_email IS NOT NULL.
 * When the same user submits again their old review is replaced (upsert).
 * Anonymous (no-email) submissions always insert a fresh row.
 *
 * googleVerified = true  → status='published_verified', published immediately.
 * googleVerified = false → status='pending'; verification token inserted if
 *                          email + wantVerification are both set.
 * newsletterOptIn + email → INSERT newsletter_subscribers.
 */
export async function submitToolReview({
  toolId, reviewerName, reviewerJobTitle, reviewerCompany,
  reviewerEmail, rating, title, body, pros, cons,
  newsletterOptIn, wantVerification, googleVerified,
  planId,   // optional — uuid of child tool (plan) being reviewed
}) {
  const displayName = reviewerName ? 'full_name' : 'anonymous';
  const now = new Date().toISOString();

  const reviewRow = {
    tool_id:                  toolId,
    reviewer_name_input:      reviewerName     || null,
    reviewer_job_title_input: reviewerJobTitle || null,
    reviewer_company_input:   reviewerCompany  || null,
    reviewer_email:           reviewerEmail    || null,
    display_name:             displayName,
    rating,
    title:                    title  || null,
    body:                     body   || null,
    pros:                     pros   || null,
    cons:                     cons   || null,
    newsletter_opt_in:        newsletterOptIn ?? false,
    show_job_title:           true,
    show_company:             false,
    // plan_id requires: ALTER TABLE reviews ADD COLUMN plan_id uuid REFERENCES tools(id);
    ...(planId ? { plan_id: planId } : {}),
    ...(googleVerified
      ? {
          status:            'published_verified',
          verification_tier: 'verified',
          published_at:      now,
        }
      : {
          status:            'pending',
          verification_tier: 'anonymous',
        }),
  };

  // If email is provided, delete any prior review by this user for this tool first
  // (enforces one review per user without requiring a DB-level unique constraint).
  if (reviewerEmail) {
    await supabase
      .from('reviews')
      .delete()
      .eq('tool_id', toolId)
      .eq('reviewer_email', reviewerEmail);
    // Intentionally ignore delete errors — row simply may not exist yet.
  }

  const { error: writeError } = await supabase.from('reviews').insert(reviewRow);

  if (writeError) {
    console.error('[submitToolReview]', writeError);
    throw writeError;
  }

  // Fetch the review id for the token (only needed for email-verification flow).
  // Wrapped in try/catch so a token failure never blocks the success response.
  if (!googleVerified && reviewerEmail && wantVerification) {
    try {
      // Use service-level lookup: select the row we just wrote via email + tool
      const { data: row } = await supabase
        .from('reviews')
        .select('id')
        .eq('tool_id', toolId)
        .eq('reviewer_email', reviewerEmail)
        .maybeSingle();

      if (row?.id) {
        const token = crypto.randomUUID();
        const expiresAt = new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString();
        await supabase.from('review_verification_tokens').insert({
          review_id:  row.id,
          token,
          email:      reviewerEmail,
          expires_at: expiresAt,
        });
      }
    } catch (tokenErr) {
      console.warn('[submitToolReview] token insert failed (non-fatal):', tokenErr);
    }
  }

  if (newsletterOptIn && reviewerEmail) {
    supabase.from('newsletter_subscribers').insert({
      email:     reviewerEmail,
      confirmed: googleVerified ?? false, // Google-verified users auto-confirm newsletter
      source:    'review_form',
    }).then(() => {});
  }
}

/**
 * Fetch all categories (used for filter strip and category pills).
 * Categories are associated with tools via: task_tool → task_usecase.category_id → categories
 */
export async function fetchCategories() {
  const { data, error } = await supabase
    .from('categories')
    .select('id, name, slug, icon')
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch all disciplines from the discipline table.
 * Returns [{ id, name }] ordered alphabetically.
 */
export async function fetchDisciplines() {
  const { data, error } = await supabase
    .from('discipline')
    .select('id, name')
    .order('name', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/**
 * Batch-fetch immediate child tool names for a set of parent tool IDs.
 * Used by the library card to show variation names as a sub-header.
 * Returns { [parentToolId]: [{ id, name, slug, tool_type, is_primary }] }
 */
export async function fetchToolChildMap(toolIds) {
  if (!toolIds.length) return {};
  const { data, error } = await supabase
    .from('tools')
    .select('id, name, slug, tool_type, parent_tool_id')   // is_primary omitted — may not exist yet
    .in('parent_tool_id', toolIds)
    .order('name', { ascending: true });
  if (error) throw error;

  const map = {};
  for (const row of data ?? []) {
    if (!map[row.parent_tool_id]) map[row.parent_tool_id] = [];
    map[row.parent_tool_id].push(row);
  }
  return map;
}

/**
 * Fetch child plans / add-ons for a parent product.
 * Plans are tools with parent_tool_id = toolId.
 * Ordered: primary plans first, then by price ascending.
 * tool_meta holds the detailed pricing for each plan.
 *
 * NOTE: v_tools_published must compute price_from for parent rows as:
 *   COALESCE(
 *     (SELECT MIN(tm.price_from) FROM tools c JOIN tool_meta tm ON tm.tool_id = c.id
 *      WHERE c.parent_tool_id = t.id),
 *     t.price_from
 *   )
 */
export async function fetchToolPlans(toolId) {
  // Only select columns that exist on the raw tools table.
  // Columns like pricing_model, price_from, price_currency, has_free_trial
  // may only exist in v_tools_published (the view) — read them from tool_meta instead.
  // Only select columns guaranteed to exist on the raw tools table.
  // All pricing/billing details come from tool_meta.
  const { data, error } = await supabase
    .from('tools')
    .select('id, name, slug, tool_type, is_primary, website_url, affiliate_url, tool_meta(*)')
    .eq('parent_tool_id', toolId)
    .order('is_primary', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/**
 * Batch-fetch categories for a set of tool IDs.
 * Path: task_tool → task_usecase.category_id → categories
 * Returns { [toolId]: deduplicated category array }
 */
export async function fetchToolCategoryMap(toolIds) {
  if (!toolIds.length) return {};
  const { data, error } = await supabase
    .from('task_tool')
    .select('tool_id, task_usecase(categories(id, name, slug))')
    .in('tool_id', toolIds);
  if (error) throw error;

  const map = {};
  for (const row of data ?? []) {
    const cat = row.task_usecase?.categories;
    if (!cat) continue;
    if (!map[row.tool_id]) map[row.tool_id] = [];
    if (!map[row.tool_id].find((c) => c.id === cat.id)) {
      map[row.tool_id].push(cat);
    }
  }
  return map;
}
